import React, { useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSpinner, FaExclamationCircle, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { usePlatformViewModel } from './viewmodel/platform-viewmodel';
import PlatformForm from './components/PlatformForm';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import { usePermissionViewModel } from '../permissions/viewmodel/permission-viewmodel';


const Platform = () => {
  const {
    platforms,
    loading,
    error,
    successMessage,
    isModalOpen,
    currentPlatform,
    formData,
    formErrors,
    isDeleteModalOpen,
    platformToDelete,
    
    // Add pagination states
    pageNumber,
    pageSize,
    totalItems,
    maxPage,
    
    loadPlatforms,
    handleInputChange,
    handleOpenCreateModal,
    handleOpenEditModal,
    handleCloseModal,
    handleSavePlatform,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    handleDeletePlatform,
    dismissError,
    dismissSuccess,
    
    // Add pagination handlers
    handlePageChange,
    handlePageSizeChange
  } = usePlatformViewModel();

  const {hasPermission} = usePermissionViewModel('platforme')

  useEffect(() => {
    // Set the page title
    document.title = 'Plateforme de commandes | Admin';
  }, []);

  // Make sure platforms is always an array for mapping
  const platformsArray = Array.isArray(platforms) ? platforms : [];

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      {/* Page header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Plateformes</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gérez les plateformes de commandes de votre application
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
          disabled={!hasPermission('write')}
            type="button"
            onClick={handleOpenCreateModal}
            className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed inline-flex items-center rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
          >
            <FaPlus className="-ml-1 mr-2 h-4 w-4" />
            Ajouter une plateforme
          </button>
        </div>
      </div>
      
      {/* Alert messages */}
      {error && (
        <div className="mb-6 rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <FaExclamationCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <div className="-mx-1.5 -my-1.5">
                <button
                  type="button"
                  onClick={dismissError}
                  className="inline-flex rounded-md bg-red-50 p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50"
                >
                  <span className="sr-only">Dismiss</span>
                  <FaPlus className="h-5 w-5 transform rotate-45" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {successMessage && (
        <div className="mb-6 rounded-md bg-green-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{successMessage}</p>
            </div>
            <div className="ml-auto pl-3">
              <div className="-mx-1.5 -my-1.5">
                <button
                  type="button"
                  onClick={dismissSuccess}
                  className="inline-flex rounded-md bg-green-50 p-1.5 text-green-500 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 focus:ring-offset-green-50"
                >
                  <span className="sr-only">Dismiss</span>
                  <FaPlus className="h-5 w-5 transform rotate-45" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Platform list */}
      <div className="mt-6 overflow-hidden bg-white shadow sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {loading && platformsArray.length === 0 ? (
            <li className="flex justify-center items-center p-12">
              <FaSpinner className="h-8 w-8 text-emerald-500 animate-spin" />
            </li>
          ) : platformsArray.length === 0 ? (
            <li className="flex flex-col items-center justify-center p-12 text-center">
              <svg className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune plateforme</h3>
              <p className="mt-1 text-sm text-gray-500">Commencez par créer une nouvelle plateforme.</p>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={handleOpenCreateModal}
                  className="inline-flex items-center rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
                >
                  <FaPlus className="-ml-1 mr-2 h-5 w-5" />
                  Ajouter une plateforme
                </button>
              </div>
            </li>
          ) : (
            platformsArray.map((platform) => (
              <li key={platform.id}>
                <div className="flex items-center justify-between px-6 py-4">
                  <div className="flex items-center">
                    <div className="w-10 h-10 flex items-center justify-center bg-emerald-100 rounded-full">
                      <span className="text-lg font-medium text-emerald-800">
                        {platform.name ? platform.name.charAt(0).toUpperCase() : '?'}
                      </span>
                    </div>
                    <div className="ml-4">
                      <div className="font-medium text-gray-900">{platform.name}</div>
                      <div className="text-sm text-gray-500">@{platform.username}</div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                    disabled={!hasPermission('edit')}
                      type="button"
                      onClick={() => handleOpenEditModal(platform)}
                      className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
                    >
                      <FaEdit className="h-4 w-4 text-gray-500" />
                      <span className="ml-2 sr-only sm:not-sr-only">Modifier</span>
                    </button>
                    <button
                    disabled={!hasPermission('delete')}
                      type="button"
                      onClick={() => handleOpenDeleteModal(platform)}
                      className="disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    >
                      <FaTrash className="h-4 w-4 text-red-500" />
                      <span className="ml-2 sr-only sm:not-sr-only">Supprimer</span>
                    </button>
                  </div>
                </div>
              </li>
            ))
          )}
        </ul>
        
        {/* Pagination Controls */}
        {platformsArray.length > 0 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(Math.max(1, pageNumber - 1))}
                disabled={pageNumber <= 1}
                className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                  pageNumber <= 1 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Précédent
              </button>
              <button
                onClick={() => handlePageChange(Math.min(maxPage, pageNumber + 1))}
                disabled={pageNumber >= maxPage}
                className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                  pageNumber >= maxPage 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Suivant
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div className="flex items-center gap-4">
                <select
                  value={pageSize}
                  onChange={e => handlePageSizeChange(Number(e.target.value))}
                  className="border border-gray-300 rounded-md text-sm py-1 pl-2 pr-7 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  {[5, 10, 25, 50].map(s => (
                    <option key={s} value={s}>{s} / page</option>
                  ))}
                </select>
                <p className="text-sm text-gray-700">
                  {totalItems > 0 ? (
                    <>
                      Affichage de <span className="font-medium">{Math.min((pageNumber - 1) * pageSize + 1, totalItems)}</span> à{' '}
                      <span className="font-medium">
                        {Math.min(pageNumber * pageSize, totalItems)}
                      </span>{' '}
                      sur <span className="font-medium">{totalItems}</span> plateformes
                    </>
                  ) : (
                    'Aucun résultat'
                  )}
                </p>
              </div>
              <div>
                {maxPage > 1 && (
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    {/* Previous Page Button */}
                    <button
                      onClick={() => handlePageChange(Math.max(1, pageNumber - 1))}
                      disabled={pageNumber <= 1}
                      className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${
                        pageNumber <= 1 
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                          : 'bg-white text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <span className="sr-only">Précédent</span>
                      <FaChevronLeft className="h-4 w-4" aria-hidden="true" />
                    </button>
                    
                    {/* Page Numbers */}
                    {[...Array(maxPage)].map((_, i) => {
                      const pageNum = i + 1;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            pageNumber === pageNum
                              ? 'z-10 bg-emerald-50 border-emerald-500 text-emerald-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    {/* Next Page Button */}
                    <button
                      onClick={() => handlePageChange(Math.min(maxPage, pageNumber + 1))}
                      disabled={pageNumber >= maxPage}
                      className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${
                        pageNumber >= maxPage 
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                          : 'bg-white text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <span className="sr-only">Suivant</span>
                      <FaChevronRight className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </nav>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Form Modal */}
      <PlatformForm
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        platform={currentPlatform}
        formData={formData}
        formErrors={formErrors}
        handleInputChange={handleInputChange}
        handleSavePlatform={handleSavePlatform}
        loading={loading}
      />
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDeletePlatform}
        platform={platformToDelete}
        loading={loading}
      />
    </div>
  );
};

export default Platform;